import React, {
  useRef,
  useMemo,
  useState,
  useCallback,
  ReactNode,
  useEffect,
} from "react";
import { withRouter } from "react-router-dom";

import {
  ActionToolbar,
  DialogWithActions,
  EditButton,
  GqlTable,
} from "../../components";

import { Button, Chip, Container, FormControlLabel, Switch } from "@material-ui/core";
import TableCell from "@material-ui/core/TableCell";

import { QUERIES } from "../../../graphql";
import { Service } from "../../../graphql/models";
import { ServiceForm } from "./service_form";
import { ServiceDeleteRestoreButton } from "./service_delete_restore_button";

import userServiceStyle from "./services.style";
import { useLoggedUserInfo, useSnackbarQuery } from "../../../hooks";
import {
  ServiceCategory,
  ServiceCategoryGqlResponse,
} from "./types/serviceCategory.type";

function AmenitiesPage() {
  const classes = userServiceStyle();

  const tableRef = useRef();

  const { isAdminUser, isServiceProviderUser } = useLoggedUserInfo();
  const [formDialogOpened, setFormDialogOpened] = useState(false);
  const [formService, setFormService] = useState<Service | undefined>(void 0);
  const [showDeleted, setShowDeleted] = useState(false);

  const addNewService = () => {
    setFormService({} as Service);
    setFormDialogOpened(true);
  };

  const editService = (row) => {
    setFormService({
      ...row,
    });
    setFormDialogOpened(true);
  };

  const onValueUpdatedSuccessfully = () => {
    setFormService(void 0);
    setFormDialogOpened(false);
    (tableRef as any).current?.refresh();
  };

  const gqlTableRowActions = useCallback(
    (row) => {
      const actions: ReactNode[] = [];
      if (isAdminUser || isServiceProviderUser) {
        actions.push(
          <EditButton key="editService" onClick={() => editService(row)} />
        );
      }

      if (isAdminUser) {
        actions.push(
          <ServiceDeleteRestoreButton
            key="deleteRestoreService"
            service={row}
            onDone={() => (tableRef as any).current?.refresh()}
          />
        );
      }

      return actions;
    },
    [isAdminUser, isServiceProviderUser]
  );

  const actionToolbarActions = useMemo(() => {
    const actions: any[] = [];
    if (isAdminUser) {
      actions.push(
        <FormControlLabel
          key="showDeleted"
          control={
            <Switch
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
              color="primary"
            />
          }
          label="Mostra eliminati"
        />,
        <Button
          key="addNewService"
          variant="contained"
          color="primary"
          onClick={() => addNewService()}
        >
          Aggiungi Nuovo
        </Button>
      );
    }
    return actions;
  }, [isAdminUser, showDeleted]);

  const { data: serviceCategoriesData } =
    useSnackbarQuery<ServiceCategoryGqlResponse>(QUERIES.SERVICE_CATEGORIES);

  const [serviceCategories, setServiceCategories] = useState<ServiceCategory[]>(
    []
  );

  useEffect(() => {
    if (!serviceCategoriesData) return;

    setServiceCategories(serviceCategoriesData.serviceCategories);
  }, [serviceCategoriesData]);

  const onServiceCategoryCreate = useCallback(
    (serviceCategory: ServiceCategory) =>
      setServiceCategories((pv) => [...pv, serviceCategory]),
    []
  );

  const onServiceCategoryUpdate = useCallback(
    (serviceCategory: ServiceCategory) =>
      setServiceCategories((pv) =>
        [...pv].map((v) => (v.id === serviceCategory.id ? serviceCategory : v))
      ),
    []
  );

  return (
    <Container className={classes.container} maxWidth={false} component="div">
      <>
        <ActionToolbar title={"Servizi"} actions={actionToolbarActions} />

        <GqlTable
          ref={tableRef}
          columns={(isAdminUser ? ["", "Nome", "Descrizione", "Ordine", "Stato"] : ["", "Nome", "Descrizione", "Ordine"]).map((v, index) => (
            <TableCell key={index}>{v}</TableCell>
          ))}
          query={
            isAdminUser
              ? QUERIES.BACKOFFICE_SERVICES_WITH_DELETED
              : QUERIES.BACKOFFICE_SERVICES
          }
          queryVariables={
            isAdminUser ? { includeDeleted: showDeleted } : undefined
          }
          queryResultDataField="backofficeServices"
          rows={(row: Service) => {
            const cellStyle = row.deleted ? { color: "#9e9e9e" } : undefined;
            return (
              <>
                <TableCell>{gqlTableRowActions(row)}</TableCell>
                <TableCell style={cellStyle}>{row.name}</TableCell>
                <TableCell style={cellStyle}>{row.description}</TableCell>
                <TableCell style={cellStyle}>{row.sortIndex ?? "-"}</TableCell>
                {isAdminUser && (
                  <TableCell>
                    {row.deleted ? (
                      <Chip size="small" label="Eliminato" />
                    ) : null}
                  </TableCell>
                )}
              </>
            );
          }}
        />

        <DialogWithActions
          fullScreen
          open={formDialogOpened}
          setOpen={setFormDialogOpened}
          title={"Aggiungi / Modifica Service"}
        >
          <ServiceForm
            value={formService}
            serviceCategories={serviceCategories}
            onValueUpdatedSuccessfully={onValueUpdatedSuccessfully}
            onServiceCategoryCreate={onServiceCategoryCreate}
            onServiceCategoryUpdate={onServiceCategoryUpdate}
          />
        </DialogWithActions>
      </>
    </Container>
  );
}

export default withRouter(AmenitiesPage);
